import apiClient from '../apiClient';
import campaignController from '../campaign';
import { setPassword, setStompUrl, setStompUser, toggleInputDisabled } from '../store/dispatcher';

/** A create in progress, so a second caller joins it rather than opening a second session. */
let inFlight: Promise<boolean> | null = null;

// True for exactly the window between a session being CREATED here and that session's first STOMP
// connect. App's connectCallback consumes it once to SKIP the connect-time reconcile (drop-all +
// refetch), which on a brand-new session races the create's own `POST /message` and would blank the
// optimistic greeting + reply. A module flag rather than an App ref because BOTH create paths flow
// through here — the reply funnel (`ensureSession`) and the pre-chat form (`ContactForm.postNewSession`).
// DC-T114-119 set an App ref in `ensureSession` only, so the form path was still unprotected; M7 moves
// the signal to this shared chokepoint so both are covered.
let sessionJustCreated = false;
export const consumeSessionJustCreated = (): boolean => {
  const v = sessionJustCreated;
  sessionJustCreated = false;
  return v;
};

/**
 * IMBEE-8954 (TDS §6b "Reply → session") — the ONE session-creation funnel.
 *
 * Two things start a conversation: the pre-chat contact form's submit, and `App.ensureSession` —
 * a reply that finds no session and no form up, whatever took the form down. (A campaign whose
 * `skipPreChatForm` removed it (AC-4.2) is why that second route has to exist, but it is not the
 * condition: `ensureSession` reads session and form state, never the engagement.) Both come
 * through here, and `apiClient.createSession` is called from nowhere else. A second caller with
 * its own call would be a divergent session path — the TDS names that as the thing this funnel
 * exists to prevent — and a reply path with NO call is a visitor whose message goes to
 * `POST /message` with an empty `session_id`, losing both the conversation and the very
 * attribution AC-4.3(b) is about.
 *
 * What the funnel owns, so both callers behave identically:
 *
 *  - the composer flip. `toggleInputDisabled` is a TOGGLE with no setter. The composer is down
 *    while the pre-chat form is up and the stomp `connectCallback` flips it back on connect, so
 *    creation must take it down first or that callback would DISABLE a composer the visitor is
 *    already typing into. On a failed create it deliberately stays down: nothing works without a
 *    session, and letting the visitor type into one that does not exist is the worse of the two.
 *  - the STOMP handoff. Writing `stompUrl` is what arms App's `[stompUrl]` effect, and a caller
 *    that forgot it would create a session with no live topic — invisible until a message never
 *    arrived.
 *  - which campaign show, if any, the session is attributed to.
 *
 * The show id is `campaignController.getEngagedShowId()` — the show the visitor is engaged with —
 * and never `getRenderState()`. `campaignController`'s `engagedShowId` states the one rule that
 * decides when that stops answering, and every consumer obeys it rather than carrying a private
 * notion of "still attributable": this is the attribution consumer, `reportOutcome('replied')` is
 * the other, and the two therefore cannot disagree. `getRenderState()` answers a different
 * question — a teaser merely displayed and never touched stays in the render state for the life
 * of the page, so reading it stamps sessions the visitor started from the launcher, and a route
 * change can swap in a different campaign's show entirely. Both directions inflate `engaged` /
 * `conversations` / `engagementRate`, and the service-side guard cannot filter either (a
 * genuinely-shown, never-outcome-reported show matches every field it checks).
 *
 * Read at call time rather than taken as an argument, deliberately: a caller that resolved an id
 * for itself would be a second copy of that rule, free to drift out of step with the first.
 */
export default function startWidgetSession(formValues: Record<string, unknown>): Promise<boolean> {
  // A create already running is JOINED, not raced. The reply path made this reachable: it fires
  // per message, and a visitor who sends a second one before the first request returns would
  // otherwise open two sessions for one conversation — and stamp the campaign show onto both.
  if (!inFlight) {
    inFlight = createTheSession(formValues).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

async function createTheSession(formValues: Record<string, unknown>): Promise<boolean> {
  toggleInputDisabled();
  const campaignShowId = campaignController.getEngagedShowId() ?? undefined;
  try {
    const response = await apiClient.createSession(formValues, campaignShowId);
    const data = response.data;
    setStompUser(data.subscribe.visitor_token);
    setPassword(data.subscribe.password);
    setStompUrl(data.subscribe.url);
    // DC-T114-119. This create arms the STOMP connect (App's [stompUrl] effect). That first
    // connect races ahead of the POST /message that created the session, so its history fetch
    // comes back empty; the connectCallback must skip its drop-and-refetch exactly once or it
    // blanks the optimistic greeting + reply. consumeSessionJustCreated() in App reads and clears
    // this. Set AFTER setStompUrl so the flag is already true by the time the effect fires.
    sessionJustCreated = true;
    // IMBEE-9231 (T86 AC-C.1). bottom_buttons is deliberately NOT read here. On create it is
    // always the unconditional set — the ticket that carries the tags does not exist until
    // core-php processes the visitor's first message — and this runs outside App, where that
    // state lives. The first real value arrives on the live topic, or on the next restore.
    return true;
  } catch (err) {
    console.log(err);
    return false;
  }
}
